import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { EmptyState } from "@/components/EmptyState";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtDate, fmtNum, todayISO, addDaysISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const EMPTY = { entry_date: todayISO(), wbs_item_id: "", trade: "", contractor: "", planned_count: "", actual_count: "", is_machinery: false, remarks: "" };

export default function Resources() {
  const { projectId, isProjectManager } = useProjectCtx();
  const [range, setRange] = useState({ start: addDaysISO(todayISO(), -30), end: todayISO() });
  const [rows, setRows] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: wbsItems }] = await Promise.all([
        api.get(`/projects/${projectId}/resources`, { params: range }),
        api.get(`/projects/${projectId}/construction`),
      ]);
      setRows(data);
      setActivities(wbsItems.filter((i) => !i.is_group));
    } finally {
      setLoading(false);
    }
  }, [projectId, range]);

  useEffect(() => { load(); }, [load]);

  const activityName = (id) => activities.find((a) => a.id === id)?.name;

  const remove = async (id) => {
    try {
      await api.delete(`/projects/${projectId}/resources/${id}`);
      toast.success("Entry removed");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="resources-page">
      <PageHeader
        title="Resource Log"
        description="Daily manpower and machinery counts by trade, optionally linked to a construction activity."
        action={
          <div className="flex items-center gap-2">
            <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="resources-start-input" className="w-36" />
            <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="resources-end-input" className="w-36" />
            <AddResourceDialog projectId={projectId} activities={activities} onAdded={load} />
          </div>
        }
      />

      <div className="rounded-xl border border-border bg-card">
        {!loading && rows.length === 0 ? (
          <EmptyState title="No resource entries in this range" testId="resources-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Date</TableHead><TableHead>Activity</TableHead><TableHead>Trade</TableHead><TableHead>Contractor</TableHead><TableHead>Planned</TableHead><TableHead>Actual</TableHead><TableHead>Type</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`resource-row-${r.id}`}>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(r.entry_date)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.wbs_item_id ? (activityName(r.wbs_item_id) || "—") : "—"}</TableCell>
                  <TableCell className="text-foreground">{r.trade}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.contractor || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtNum(r.planned_count)}</TableCell>
                  <TableCell className="font-mono text-xs font-semibold text-foreground">{fmtNum(r.actual_count)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.is_machinery ? "Machinery" : "Manpower"}</TableCell>
                  <TableCell className="text-right">
                    {isProjectManager && (
                      <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`delete-resource-btn-${r.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function AddResourceDialog({ projectId, activities, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const submit = async () => {
    try {
      await api.post(`/projects/${projectId}/resources`, {
        ...form,
        wbs_item_id: form.wbs_item_id || null,
        planned_count: form.planned_count ? Number(form.planned_count) : null,
        actual_count: Number(form.actual_count || 0),
      });
      toast.success("Entry saved");
      setForm(EMPTY);
      setOpen(false);
      onAdded();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="add-resource-button"><Plus className="h-3.5 w-3.5" /> Log entry</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Daily resource entry</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Date</Label><Input type="date" data-testid="resource-date-input" value={form.entry_date} onChange={set("entry_date")} className="mt-1.5" /></div>
          <div>
            <Label>Activity (optional)</Label>
            <Select value={form.wbs_item_id || "__none__"} onValueChange={(v) => setForm({ ...form, wbs_item_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="resource-activity-select"><SelectValue placeholder="Not linked to an activity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Not linked</SelectItem>
                {activities.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Trade</Label><Input required data-testid="resource-trade-input" value={form.trade} onChange={set("trade")} className="mt-1.5" placeholder="Electrician" /></div>
            <div><Label>Contractor</Label><Input data-testid="resource-contractor-input" value={form.contractor} onChange={set("contractor")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Planned count</Label><Input type="number" data-testid="resource-planned-input" value={form.planned_count} onChange={set("planned_count")} className="mt-1.5" /></div>
            <div><Label>Actual count</Label><Input type="number" required data-testid="resource-actual-input" value={form.actual_count} onChange={set("actual_count")} className="mt-1.5" /></div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.is_machinery} onCheckedChange={(v) => setForm({ ...form, is_machinery: v })} data-testid="resource-machinery-switch" />
            <Label>This is machinery, not manpower</Label>
          </div>
          <div><Label>Remarks</Label><Input data-testid="resource-remarks-input" value={form.remarks} onChange={set("remarks")} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button disabled={!form.trade || form.actual_count === ""} onClick={submit} data-testid="resource-submit-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
